import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Shield, FileText, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Police logo URL - permanent
const POLICE_LOGO_URL = 'https://customer-assets.emergentagent.com/job_1edf88dc-3fd5-40c7-b1aa-85fddbf9a215/artifacts/0qau0qow_Politiet_logo_sort.png';

// Weapon types for dropdown
const WEAPON_TYPES = [
  'Rifle',
  'Pistol',
  'Hagle',
  'Revolver',
  'Luftgevær',
  'Luftpistol',
  'Løpsdel',
  'Låskasse',
  'Ammunisjon',
  'Annet'
];

// Function modes for dropdown
const FUNCTION_MODES = [
  'Semi-automatisk',
  'Manuell repeterende',
  'Enkeltskudd',
  'Hel-automatisk',
  'Ikke aktuelt'
];

const WeaponLoanForm = () => {
  const { user, isAdmin, isOrgAdmin, isRangeOfficer } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Check for weapon ID from URL
  const weaponId = searchParams.get('weapon');
  
  // Form state
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [weapon, setWeapon] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [allWeapons, setAllWeapons] = useState([]); // All club weapons for dropdown
  
  // Lender info (autofilled for organization loans)
  const [lenderName, setLenderName] = useState('');
  const [lenderBirthdate, setLenderBirthdate] = useState('');
  const [lenderWeaponCardNumber, setLenderWeaponCardNumber] = useState('');
  const [lenderIssuedBy, setLenderIssuedBy] = useState('');
  
  // Borrower info
  const [borrowerName, setBorrowerName] = useState('');
  const [borrowerBirthdate, setBorrowerBirthdate] = useState('');
  const [borrowerWeaponCardNumber, setBorrowerWeaponCardNumber] = useState('');
  const [borrowerIssuedBy, setBorrowerIssuedBy] = useState('');
  const [borrowerIdDocument, setBorrowerIdDocument] = useState('');
  
  // Weapon info (can be autofilled from scanned weapon)
  const [weaponItems, setWeaponItems] = useState([
    { weaponId: '', weaponType: '', make: '', model: '', caliber: '', serialNumber: '', functionMode: '' }
  ]);
  
  // Usage and duration
  const [usageArea, setUsageArea] = useState('');
  const [loanFromDate, setLoanFromDate] = useState('');
  const [loanToDate, setLoanToDate] = useState('');
  
  // Signatures
  const [lenderSignature, setLenderSignature] = useState('');
  const [borrowerSignature, setBorrowerSignature] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Check access
  const hasAccess = isAdmin || isOrgAdmin || isRangeOfficer;
  
  // Load weapon and organization data
  useEffect(() => {
    const loadData = async () => {
      if (!hasAccess) return;
      
      setLoading(true);
      const token = localStorage.getItem('token');
      
      try {
        // Load organization info
        try {
          const orgResponse = await axios.get(`${API_BASE}/api/organization`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setOrganization(orgResponse.data);
          
          // Autofill lender info from organization
          setLenderName(orgResponse.data.name || '');
          setLenderWeaponCardNumber(orgResponse.data.weapon_card_number || '');
          setLenderIssuedBy(orgResponse.data.police_district || '');
        } catch (orgErr) {
          console.warn('Could not load organization info:', orgErr);
        }
        
        // Load ALL club weapons for dropdown
        const weaponsResponse = await axios.get(`${API_BASE}/api/club-operations/weapons`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Filter out already loaned weapons (unless it's the weapon we're viewing)
        const availableWeapons = weaponsResponse.data.filter(w => 
          !w.is_loaned || w.id === weaponId
        );
        setAllWeapons(availableWeapons);
        
        // Load weapon if ID provided
        if (weaponId) {
          const weaponResponse = await axios.get(`${API_BASE}/api/club-operations/weapons/${weaponId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setWeapon(weaponResponse.data);
          
          // Autofill weapon info
          setWeaponItems([{
            weaponId: weaponResponse.data.id,
            weaponType: determineWeaponType(weaponResponse.data.caliber),
            make: weaponResponse.data.brand || '',
            model: weaponResponse.data.model || '',
            caliber: weaponResponse.data.caliber || '',
            serialNumber: weaponResponse.data.serial_number || '',
            functionMode: weaponResponse.data.function_mode || ''
          }]);
        }
        
        // Set default dates
        const today = new Date().toISOString().split('T')[0];
        setLoanFromDate(today);
        
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Kunne ikke laste data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [weaponId, hasAccess]);
  
  // Helper to determine weapon type from caliber
  const determineWeaponType = (caliber) => {
    if (!caliber) return '';
    const calLower = caliber.toLowerCase();
    if (calLower.includes('22') || calLower.includes('.22')) return 'Pistol';
    if (calLower.includes('9mm') || calLower.includes('9x19')) return 'Pistol';
    if (calLower.includes('.45') || calLower.includes('45acp')) return 'Pistol';
    if (calLower.includes('12ga') || calLower.includes('hagle')) return 'Hagle';
    if (calLower.includes('.308') || calLower.includes('308win') || calLower.includes('30-06')) return 'Rifle';
    if (calLower.includes('.223') || calLower.includes('5.56')) return 'Rifle';
    return '';
  };
  
  // Handle weapon selection from dropdown
  const handleWeaponSelect = (index, selectedWeaponId) => {
    if (!selectedWeaponId) {
      // Clear the row if no weapon selected
      updateWeaponItem(index, 'weaponId', '');
      updateWeaponItem(index, 'make', '');
      updateWeaponItem(index, 'model', '');
      updateWeaponItem(index, 'caliber', '');
      updateWeaponItem(index, 'serialNumber', '');
      updateWeaponItem(index, 'weaponType', '');
      updateWeaponItem(index, 'functionMode', '');
      return;
    }
    
    const selectedWeapon = allWeapons.find(w => w.id === selectedWeaponId);
    if (selectedWeapon) {
      const updated = [...weaponItems];
      updated[index] = {
        weaponId: selectedWeapon.id,
        weaponType: determineWeaponType(selectedWeapon.caliber),
        make: selectedWeapon.brand || '',
        model: selectedWeapon.model || '',
        caliber: selectedWeapon.caliber || '',
        serialNumber: selectedWeapon.serial_number || '',
        functionMode: selectedWeapon.function_mode || ''
      };
      setWeaponItems(updated);
    }
  };
  
  // Add weapon item row
  const addWeaponItem = () => {
    if (weaponItems.length < 10) {
      setWeaponItems([...weaponItems, { weaponId: '', weaponType: '', make: '', model: '', caliber: '', serialNumber: '', functionMode: '' }]);
    }
  };
  
  // Update weapon item
  const updateWeaponItem = (index, field, value) => {
    const updated = [...weaponItems];
    updated[index][field] = value;
    setWeaponItems(updated);
  };
  
  // Remove weapon item
  const removeWeaponItem = (index) => {
    if (weaponItems.length > 1) {
      setWeaponItems(weaponItems.filter((_, i) => i !== index));
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!borrowerName || !borrowerBirthdate || !borrowerWeaponCardNumber) {
      toast.error('Vennligst fyll ut alle obligatoriske felt for låntaker');
      return;
    }
    
    if (!loanFromDate || !loanToDate) {
      toast.error('Vennligst angi utlånsperiode');
      return;
    }
    
    if (!lenderSignature || !borrowerSignature) {
      toast.error('Begge parter må signere');
      return;
    }
    
    if (!agreedToTerms) {
      toast.error('Du må godta vilkårene');
      return;
    }
    
    // Validate at least one weapon
    const validWeapons = weaponItems.filter(w => w.make && w.serialNumber);
    if (validWeapons.length === 0) {
      toast.error('Vennligst legg til minst ett våpen');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Create loan record
      const loanData = {
        lender: {
          name: lenderName,
          birthdate: lenderBirthdate,
          weapon_card_number: lenderWeaponCardNumber,
          issued_by: lenderIssuedBy
        },
        borrower: {
          name: borrowerName,
          birthdate: borrowerBirthdate,
          weapon_card_number: borrowerWeaponCardNumber,
          issued_by: borrowerIssuedBy,
          id_document: borrowerIdDocument
        },
        weapons: validWeapons.map(w => ({
          weapon_type: w.weaponType,
          make: w.make,
          model: w.model,
          caliber: w.caliber,
          serial_number: w.serialNumber,
          function_mode: w.functionMode,
          weapon_id: weapon?.id // Link to weapon in system if available
        })),
        usage_area: usageArea,
        loan_from_date: loanFromDate,
        loan_to_date: loanToDate,
        lender_signature: lenderSignature,
        borrower_signature: borrowerSignature,
        signed_at: new Date().toISOString()
      };
      
      await axios.post(`${API_BASE}/api/club-operations/weapons/loan`, loanData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(true);
      toast.success('Utlånserklæring registrert');
      
      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/club-operations');
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting loan:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke registrere utlån');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Get redirect URL based on role
  const getBackUrl = () => {
    return hasAccess ? '/club-operations' : '/home';
  };
  
  // Access denied
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg p-8 max-w-md text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Ingen tilgang</h2>
          <p className="text-gray-600 mb-4">Du har ikke tilgang til denne siden.</p>
          <a href="/home" className="text-blue-600 hover:underline">Tilbake til hjem</a>
        </div>
      </div>
    );
  }
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
      </div>
    );
  }
  
  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg p-8 max-w-md text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Utlån registrert!</h2>
          <p className="text-gray-600 mb-4">Utlånserklæringen er nå lagret i systemet.</p>
          <p className="text-gray-500 text-sm">Omdirigerer tilbake...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gray-800 p-4 flex items-center justify-between sticky top-0 z-10">
        <a href={getBackUrl()} className="flex items-center gap-2 text-gray-300 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
          <span>Tilbake</span>
        </a>
        <div className="flex items-center gap-2 text-yellow-500">
          <FileText className="w-5 h-5" />
          <span className="font-bold">Utlånserklæring</span>
        </div>
        <div className="w-20"></div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Police Header */}
          <div className="bg-white p-6 border-b-2 border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <img 
                src={POLICE_LOGO_URL} 
                alt="Politiet" 
                className="h-16 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <h1 className="text-2xl font-bold text-gray-900">Utlånserklæring</h1>
            </div>
            
            <div className="text-sm text-gray-600 space-y-2">
              <p>
                Dette skjema benyttes når det lånes ut våpen jf. våpenloven § 18 første ledd, 
                jf. våpenforskriften (2021) § 6-9. Ved utfylling av skjema gjøres det oppmerksom på følgende:
              </p>
              <ol className="list-decimal list-inside space-y-1 ml-4">
                <li>Utlåner er ansvarlig for at informasjonen påført skjema er fullstendig og korrekt før utlån finner sted.</li>
                <li>Utlåner og låntaker plikter å oppbevare hver sin kopi av utlånserklæring for kontroll.</li>
              </ol>
            </div>
          </div>

          {/* Section 1: Lender */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span>
              Utlåner
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Navn:</label>
                <input
                  type="text"
                  value={lenderName}
                  onChange={(e) => setLenderName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Klubb/organisasjon eller personnavn"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fødselsdato (6 siffer) / Org.nr:</label>
                <input
                  type="text"
                  value={lenderBirthdate}
                  onChange={(e) => setLenderBirthdate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="DDMMÅÅ eller org.nr"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Våpenkortnummer:</label>
                <input
                  type="text"
                  value={lenderWeaponCardNumber}
                  onChange={(e) => setLenderWeaponCardNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Våpenkortnummer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Utstedt av:</label>
                <input
                  type="text"
                  value={lenderIssuedBy}
                  onChange={(e) => setLenderIssuedBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Politidistrikt"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Borrower */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">2</span>
              Låntaker
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Navn: <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={borrowerName}
                  onChange={(e) => setBorrowerName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Fullt navn"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fødselsdato (6 siffer): <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={borrowerBirthdate}
                  onChange={(e) => setBorrowerBirthdate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="DDMMÅÅ"
                  maxLength={6}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Våpenkortnummer: <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={borrowerWeaponCardNumber}
                  onChange={(e) => setBorrowerWeaponCardNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Våpenkortnummer"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Utstedt av:</label>
                <input
                  type="text"
                  value={borrowerIssuedBy}
                  onChange={(e) => setBorrowerIssuedBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Politidistrikt"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Identitetsdokument:</label>
                <input
                  type="text"
                  value={borrowerIdDocument}
                  onChange={(e) => setBorrowerIdDocument(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="F.eks. Pass, Førerkort, Bankkort med bilde"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Weapons */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">3</span>
              Informasjon om våpen, våpendeler eller ammunisjon som lånes ut
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-2 py-2 text-left font-medium text-gray-700 border">#</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-700 border min-w-[200px]">Velg våpen fra liste</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-700 border">Våpentype</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-700 border">Merke</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-700 border">Modell</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-700 border">Kaliber</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-700 border">Serienummer</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-700 border">Funksjonsmåte</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-700 border w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {weaponItems.map((item, index) => (
                    <tr key={index}>
                      <td className="px-2 py-1 border text-center text-gray-500">{index + 1}</td>
                      <td className="px-1 py-1 border">
                        <select
                          value={item.weaponId || ''}
                          onChange={(e) => handleWeaponSelect(index, e.target.value)}
                          className="w-full px-1 py-1 border-0 focus:ring-1 focus:ring-blue-500 text-sm bg-blue-50"
                        >
                          <option value="">-- Velg våpen --</option>
                          {allWeapons
                            .filter(w => !weaponItems.some((wi, i) => i !== index && wi.weaponId === w.id))
                            .map(w => (
                              <option key={w.id} value={w.id}>
                                {w.brand} {w.model} ({w.caliber}) - S/N: {w.serial_number}
                              </option>
                            ))}
                          <option value="manual">-- Skriv inn manuelt --</option>
                        </select>
                      </td>
                      <td className="px-1 py-1 border">
                        <select
                          value={item.weaponType}
                          onChange={(e) => updateWeaponItem(index, 'weaponType', e.target.value)}
                          className="w-full px-1 py-1 border-0 focus:ring-1 focus:ring-blue-500 text-sm"
                          disabled={item.weaponId && item.weaponId !== 'manual'}
                        >
                          <option value="">Velg...</option>
                          {WEAPON_TYPES.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-1 py-1 border">
                        <input
                          type="text"
                          value={item.make}
                          onChange={(e) => updateWeaponItem(index, 'make', e.target.value)}
                          className="w-full px-1 py-1 border-0 focus:ring-1 focus:ring-blue-500 text-sm bg-gray-50"
                          placeholder="Merke"
                          readOnly={item.weaponId && item.weaponId !== 'manual'}
                        />
                      </td>
                      <td className="px-1 py-1 border">
                        <input
                          type="text"
                          value={item.model}
                          onChange={(e) => updateWeaponItem(index, 'model', e.target.value)}
                          className="w-full px-1 py-1 border-0 focus:ring-1 focus:ring-blue-500 text-sm bg-gray-50"
                          placeholder="Modell"
                          readOnly={item.weaponId && item.weaponId !== 'manual'}
                        />
                      </td>
                      <td className="px-1 py-1 border">
                        <input
                          type="text"
                          value={item.caliber}
                          onChange={(e) => updateWeaponItem(index, 'caliber', e.target.value)}
                          className="w-full px-1 py-1 border-0 focus:ring-1 focus:ring-blue-500 text-sm bg-gray-50"
                          placeholder="Kaliber"
                          readOnly={item.weaponId && item.weaponId !== 'manual'}
                        />
                      </td>
                      <td className="px-1 py-1 border">
                        <input
                          type="text"
                          value={item.serialNumber}
                          onChange={(e) => updateWeaponItem(index, 'serialNumber', e.target.value)}
                          className="w-full px-1 py-1 border-0 focus:ring-1 focus:ring-blue-500 text-sm bg-gray-50"
                          placeholder="S/N"
                          readOnly={item.weaponId && item.weaponId !== 'manual'}
                        />
                      </td>
                      <td className="px-1 py-1 border">
                        <select
                          value={item.functionMode}
                          onChange={(e) => updateWeaponItem(index, 'functionMode', e.target.value)}
                          className="w-full px-1 py-1 border-0 focus:ring-1 focus:ring-blue-500 text-sm"
                        >
                          <option value="">Velg...</option>
                          {FUNCTION_MODES.map(mode => (
                            <option key={mode} value={mode}>{mode}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-1 py-1 border text-center">
                        {weaponItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeWeaponItem(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            &times;
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {weaponItems.length < 10 && (
              <button
                type="button"
                onClick={addWeaponItem}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                + Legg til våpen
              </button>
            )}
            
            {allWeapons.length === 0 && (
              <p className="mt-2 text-sm text-gray-500">
                Ingen tilgjengelige våpen funnet i klubbens register.
              </p>
            )}
          </div>

          {/* Section 4: Usage Area */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">4</span>
              Bruksområde
            </h2>
            <textarea
              value={usageArea}
              onChange={(e) => setUsageArea(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              placeholder="Beskriv formålet med utlånet (f.eks. sportsskyting, jakt, trening, konkurranse)"
            />
          </div>

          {/* Section 5: Duration */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">5</span>
              Utlånets varighet
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Utlånt fra dato: <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={loanFromDate}
                  onChange={(e) => setLoanFromDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Utlånt til dato: <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={loanToDate}
                  onChange={(e) => setLoanToDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min={loanFromDate}
                  required
                />
              </div>
            </div>
          </div>

          {/* Section 6: Signatures */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">6</span>
              Signatur
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Utlåner (skriv fullt navn): <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={lenderSignature}
                  onChange={(e) => setLenderSignature(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-handwriting"
                  placeholder="Signatur utlåner"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ved å skrive ditt navn bekrefter du elektronisk signering
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Låntaker (skriv fullt navn): <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={borrowerSignature}
                  onChange={(e) => setBorrowerSignature(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-handwriting"
                  placeholder="Signatur låntaker"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ved å skrive ditt navn bekrefter du elektronisk signering
                </p>
              </div>
            </div>
            
            <div className="mt-4">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-gray-700">
                  Jeg bekrefter at informasjonen i dette skjemaet er korrekt og at begge parter har lest og forstått 
                  vilkårene for utlån i henhold til våpenloven § 18 og våpenforskriften § 6-9.
                </span>
              </label>
            </div>
          </div>

          {/* Submit */}
          <div className="p-6 bg-gray-50">
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Registrerer...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Registrer utlån
                </>
              )}
            </button>
          </div>
        </form>

        {/* Footer */}
        <div className="mt-4 text-center text-sm text-gray-500">
          Side 1 av 1
        </div>
      </div>
    </div>
  );
};

export default WeaponLoanForm;
